/**
 * Scheduled neighbours scans: a schedule stores the scan the Neighbours →
 * Run Scan tab would start (selection, interval, channels, batch size).
 * The selection is resolved against the site's connected jnet2 devices
 * when each run starts.
 */
import { DEFAULT_NEIGHBOR_SCAN_BATCH } from '../components/NeighborScanBatchField';

export const NEIGHBOR_SCAN_SELECTIONS = [
  { value: 'all', label: 'All eligible devices (connected Nodes)' },
  { value: 'all_caps', label: 'All CAPs' },
  { value: 'all_nodes', label: 'All Nodes' },
  { value: 'specific', label: 'Specific devices' },
];

export const DEFAULT_NEIGHBOR_SCAN_FORM = Object.freeze({
  selection: 'all',
  serialNumbers: [],
  chanScanInterval: 15,
  allFreq: true,
  batchSize: DEFAULT_NEIGHBOR_SCAN_BATCH,
});

const isCap = (d) => String(d?.capabilities?.model || '').toLowerCase().includes('cap');
const isJnet2 = (d) => String(d?.capabilities?.compatible || '').toLowerCase() === 'jnet2';

/** Devices a schedule may pick: jnet2 devices, CAPs and Nodes apart, connected first. */
export function neighborScanScheduleDevices(devices) {
  const list = (devices || []).filter((d) => d?.serial_number && isJnet2(d));
  const key = (d) => `${d.connected ? 0 : 1}${(d.name || d.serial_number).toLowerCase()}`;
  return {
    caps: list.filter(isCap).sort((a, b) => key(a).localeCompare(key(b))),
    nodes: list.filter((d) => !isCap(d)).sort((a, b) => key(a).localeCompare(key(b))),
  };
}

/** Form state from a stored request (missing fields → defaults). */
export function neighborScanFormFromRequest(req) {
  const r = req || {};
  const sel = NEIGHBOR_SCAN_SELECTIONS.some((o) => o.value === r.selection) ? r.selection : 'all';
  return {
    selection: sel,
    serialNumbers: Array.isArray(r.serial_numbers) ? r.serial_numbers.filter(Boolean) : [],
    chanScanInterval: Number(r.chan_scan_interval) > 0 ? Number(r.chan_scan_interval) : DEFAULT_NEIGHBOR_SCAN_FORM.chanScanInterval,
    allFreq: typeof r.all_freq === 'boolean' ? r.all_freq : true,
    batchSize: Number(r.batch_size) > 0 ? Number(r.batch_size) : 0,
  };
}

/** Stored request from the form state. */
export function neighborScanFormToRequest(form) {
  const f = { ...DEFAULT_NEIGHBOR_SCAN_FORM, ...(form || {}) };
  const req = {
    selection: f.selection,
    chan_scan_interval: Number(f.chanScanInterval) || DEFAULT_NEIGHBOR_SCAN_FORM.chanScanInterval,
    all_freq: Boolean(f.allFreq),
    batch_size: Number(f.batchSize) > 0 ? Number(f.batchSize) : 0,
  };
  if (f.selection === 'specific') req.serial_numbers = f.serialNumbers;
  return req;
}

/** First problem with the form, or ''. */
export function neighborScanFormProblem(form) {
  if (form?.selection === 'specific' && (form.serialNumbers || []).length === 0) return 'Select at least one device, or choose all devices.';
  const n = Number(form?.chanScanInterval);
  if (!(n >= 1 && n <= 60)) return 'The scan interval must be 1–60 seconds per channel.';
  return '';
}

/** "All CAPs" / "12 devices" for a stored request. */
export function describeNeighborScanSelection(req) {
  const r = req || {};
  switch (r.selection) {
    case 'all_caps': return 'All CAPs';
    case 'all_nodes': return 'All Nodes';
    case 'specific': {
      const n = (r.serial_numbers || []).length;
      return `${n} device${n === 1 ? '' : 's'}`;
    }
    default: return 'All eligible devices';
  }
}

/** "15 s per channel · all channels · 5 at a time" for a stored request. */
export function describeNeighborScanRequest(req) {
  const f = neighborScanFormFromRequest(req);
  const batch = f.batchSize > 0 ? `${f.batchSize} at a time` : 'all at once';
  return `${f.chanScanInterval} s per channel · ${f.allFreq ? 'all channels' : 'known channels'} · ${batch}`;
}
